import { gql, useQuery } from '@apollo/client';
import { formatAsMoney } from '@arcadiapower/warbler';
import { Flex_FetchPromoInfoQuery, Flex_FetchPromoInfoQueryVariables } from '~/typings/graphql';
import { BannerWrapper, BannerCopy } from './promo-banner.style';

export const FETCH_PROMO_INFO = gql`
  query Flex_FetchPromoInfo($promoCode: String!, $utilityId: Int) {
    promoCode: userPromoCode(promoCode: $promoCode, utilityId: $utilityId) {
      incentiveDescription
      fulfillmentAvailable
      fulfillmentStep
      isUserReferral
      referralValueCents
      referredByUserName
    }
  }
`;

const getPromoBannerCopy = promoCode => {
  const {
    fulfillmentStep,
    incentiveDescription,
    isUserReferral,
    referredByUserName,
    referralValueCents,
  } = promoCode;

  const firstSentenceCopy = isUserReferral
    ? `${referredByUserName} gave you clean energy and ${formatAsMoney(referralValueCents, {
        conversion: 'fromCents',
        minDecimals: 0,
      })}.`
    : `Your ${incentiveDescription} is waiting.`;

  switch (fulfillmentStep) {
    case 'billing':
      return `${firstSentenceCopy} Start paying your power bill through Arcadia to claim it.`;
    case 'utility_connect':
      return `${firstSentenceCopy} Create your account and connect your utility account to claim it.`;
    default:
      return `${firstSentenceCopy} Set up your Arcadia account to claim it.`;
  }
};

type PromoBannerProps = {
  promoCode?: string;
  step: string;
  utilityId?: number;
};

export const PromoBanner = ({ promoCode, step, utilityId }: PromoBannerProps) => {
  const hideComponent = !promoCode || ['bouncer', 'utility', 'dashboardRedirect'].includes(step);

  const { data, loading, error } = useQuery<
    Flex_FetchPromoInfoQuery,
    Flex_FetchPromoInfoQueryVariables
  >(FETCH_PROMO_INFO, {
    // @ts-expect-error Type 'string | undefined' is not assignable to type 'string'.
    variables: { promoCode, utilityId },
    skip: hideComponent,
  });

  if (
    hideComponent ||
    loading ||
    error ||
    !data?.promoCode.fulfillmentAvailable ||
    (!data?.promoCode.incentiveDescription && !data.promoCode.isUserReferral)
  )
    return null;

  return (
    <BannerWrapper data-testid="promo-banner">
      <BannerCopy>{getPromoBannerCopy(data.promoCode)}</BannerCopy>
    </BannerWrapper>
  );
};
